import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import './ProdutoList.css';
import './OrcamentoList.css';
import api from '../services/api';
import { toast } from 'react-toastify';

const UNIDADES = ['UND', 'M²', 'M', 'CJ', 'SV', 'VB', 'KG'];
const TIPOS_ITEM = ['Produto', 'Serviço'];

const STATUS_BADGE = {
  Pendente:  { cls: 'badge-warn',  label: 'Pendente' },
  Aprovado:  { cls: 'badge-ok',    label: 'Aprovado' },
  Rejeitado: { cls: 'badge-error', label: 'Rejeitado' },
};

const FORM_EMPTY = {
  cliente:  '',
  data:     new Date().toISOString().split('T')[0],
  validade: '',
  status:   'Pendente',
};

function OrcamentoList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const clienteFiltroId = searchParams.get('cliente');

  const [orcamentos, setOrcamentos]             = useState([]);
  const [clientes, setClientes]                 = useState([]);
  const [produtos, setProdutos]                 = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [saving, setSaving]                     = useState(false);
  const [orcamentoImpressao, setOrcamentoImpressao] = useState(null);
  const [emailModal, setEmailModal]             = useState(null);
  const [emailInput, setEmailInput]             = useState('');
  const [sendingEmail, setSendingEmail]         = useState(false);

  // form state
  const [form, setForm]     = useState(FORM_EMPTY);
  const [itens, setItens]   = useState([]);
  const [editingId, setEditingId] = useState(null);

  // item-row state
  const [especificacao,   setEspecificacao]   = useState('');
  const [unidade,         setUnidade]         = useState('UND');
  const [tipoItem,        setTipoItem]        = useState('Produto');
  const [produtoItem,     setProdutoItem]     = useState('');
  const [quantidade,      setQuantidade]      = useState('');
  const [valorUnitario,   setValorUnitario]   = useState('');
  const [editingItemIdx,  setEditingItemIdx]  = useState(null);

  const printRef = useRef();
  const formRef  = useRef();
  const [minhaEmpresa, setMinhaEmpresa] = useState(null);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

  useEffect(() => {
    fetchOrcamentos();
    fetchClientes();
    fetchProdutos();
    api.get('/perfil/', { headers: headers() })
      .then((res) => setMinhaEmpresa(res.data.empresa))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchClientes = async () => {
    try {
      const res = await api.get('/clientes/', { headers: headers() });
      setClientes(res.data);
    } catch {
      toast.error('Erro ao carregar clientes.');
    }
  };

  const fetchProdutos = async () => {
    try {
      const res = await api.get('/produtos/', { headers: headers() });
      setProdutos(res.data);
    } catch {
      toast.error('Erro ao carregar produtos.');
    }
  };

  const fetchOrcamentos = async () => {
    try {
      const res = await api.get('/orcamentos/', { headers: headers() });
      setOrcamentos(res.data);
    } catch {
      toast.error('Erro ao carregar os orçamentos.');
    } finally {
      setLoading(false);
    }
  };

  // ── Itens helpers ────────────────────────────────────────────────────────────

  const clearItemForm = () => {
    setEspecificacao('');
    setUnidade('UND');
    setTipoItem('Produto');
    setProdutoItem('');
    setQuantidade('');
    setValorUnitario('');
    setEditingItemIdx(null);
  };

  const handleSaveItem = () => {
    if (!especificacao || !quantidade || !valorUnitario ||
        parseFloat(quantidade) <= 0 || parseFloat(valorUnitario) <= 0) {
      toast.warning('Preencha todos os campos do item corretamente!');
      return;
    }
    const novoItem = {
      tipo:           tipoItem,
      produto:        tipoItem === 'Produto' && produtoItem ? produtoItem : null,
      descricao:      especificacao,
      especificacao:  unidade,
      quantidade:     parseFloat(quantidade),
      valor_unitario: parseFloat(valorUnitario),
      valor_total:    parseFloat(quantidade) * parseFloat(valorUnitario),
    };
    if (editingItemIdx !== null) {
      setItens(itens.map((item, i) => (i === editingItemIdx ? novoItem : item)));
    } else {
      setItens([...itens, novoItem]);
    }
    clearItemForm();
  };

  const handleEditItem = (index) => {
    const item = itens[index];
    setEspecificacao(item.descricao);
    setUnidade(item.especificacao);
    setTipoItem(item.tipo || 'Produto');
    setProdutoItem(item.produto || '');
    setQuantidade(String(item.quantidade));
    setValorUnitario(String(item.valor_unitario));
    setEditingItemIdx(index);
  };

  const removerItem = (index) => {
    if (editingItemIdx === index) clearItemForm();
    setItens(itens.filter((_, i) => i !== index));
  };

  const calcularTotal = () =>
    itens.reduce((acc, item) => acc + parseFloat(item.valor_total), 0);

  // ── Edit ─────────────────────────────────────────────────────────────────────

  const handleEdit = (orc) => {
    setEditingId(orc.id);
    setForm({
      cliente:  orc.cliente,
      data:     orc.data,
      validade: orc.validade,
      status:   orc.status,
    });
    setItens((orc.itens || []).map((item) => ({
      tipo:           item.tipo || 'Produto',
      produto:        item.produto || null,
      descricao:      item.descricao,
      especificacao:  item.especificacao,
      quantidade:     parseFloat(item.quantidade),
      valor_unitario: parseFloat(item.valor_unitario),
      valor_total:    parseFloat(item.valor_total),
    })));
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setForm(FORM_EMPTY);
    setItens([]);
    clearItemForm();
  };

  // ── Submit (create or update) ─────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (itens.length === 0) {
      toast.warning('Adicione ao menos um item ao orçamento.');
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      valor_total: calcularTotal().toFixed(2),
      itens,
    };
    try {
      if (editingId) {
        const res = await api.put(
          `/orcamentos/${editingId}/`,
          payload,
          { headers: { ...headers(), 'Content-Type': 'application/json' } }
        );
        setOrcamentos(orcamentos.map((o) => (o.id === editingId ? res.data : o)));
        toast.success('Orçamento atualizado com sucesso!');
        handleCancelEdit();
      } else {
        const res = await api.post(
          '/orcamentos/',
          payload,
          { headers: { ...headers(), 'Content-Type': 'application/json' } }
        );
        setOrcamentos([...orcamentos, res.data]);
        setForm(FORM_EMPTY);
        setItens([]);
        toast.success('Orçamento criado com sucesso!');
      }
    } catch (err) {
      if (err._planLimitHandled) return;
      toast.error(editingId ? 'Erro ao atualizar o orçamento.' : 'Erro ao criar o orçamento.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este orçamento?')) return;
    try {
      await api.delete(`/orcamentos/${id}/`, { headers: headers() });
      setOrcamentos(orcamentos.filter((o) => o.id !== id));
      if (editingId === id) handleCancelEdit();
      toast.success('Orçamento excluído com sucesso.');
    } catch {
      toast.error('Falha ao excluir o orçamento.');
    }
  };

  // ── Print / Email ─────────────────────────────────────────────────────────────

  const handlePrint = (orc) => {
    setOrcamentoImpressao(orc);
    setTimeout(() => {
      const printContents = printRef.current.innerHTML;
      const win = window.open('', '', 'height=900,width=900');
      win.document.write('<html><head><title>Orçamento</title>');
      win.document.write(`<style>
        body { font-family: Arial, sans-serif; margin: 20px; font-size: 13px; }
        table { width: 100%; border-collapse: collapse; margin-top: 15px; }
        th, td { border: 1px solid #000; padding: 6px 8px; }
        th { background: #c8c8c8; text-align: center; font-weight: bold; font-size: 12px; }
        td.center { text-align: center; }
        td.right  { text-align: right; }
        td.desc   { text-align: left; vertical-align: top; }
        .print-header { display: flex; justify-content: space-between; align-items: flex-start;
          border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 10px; }
        .empresa-nome { font-size: 22px; font-weight: bold; color: #00008B; }
        .empresa-info { font-size: 11px; margin-top: 4px; }
        .orc-info { text-align: right; }
        .orc-title { font-size: 18px; font-weight: bold; border: 1px solid #000;
          padding: 4px 16px; display: inline-block; }
        .client-row { margin: 10px 0; display: flex; justify-content: space-between; }
        tfoot td { font-weight: bold; background: #c8c8c8; }
        .nota { margin-top: 8px; font-size: 12px; font-weight: bold; }
        .footer-sign { margin-top: 60px; text-align: center; }
        .sign-line { border-top: 1px solid #000; width: 60%; margin: 0 auto;
          padding-top: 6px; font-size: 11px; }
      </style>`);
      win.document.write('</head><body>');
      win.document.write(printContents);
      win.document.write('</body></html>');
      win.document.close();
      win.focus();
      win.print();
      win.close();
    }, 150);
  };

  const handleEmail = (orc) => {
    setEmailInput(orc.cliente_email || '');
    setEmailModal(orc);
  };

  const handleDownloadPdf = async (orc) => {
    try {
      const res = await api.get(`/orcamentos/${orc.id}/pdf/`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `orcamento_${orc.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Erro ao gerar o PDF do orçamento.');
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!emailInput || !emailModal) return;
    setSendingEmail(true);
    try {
      await api.post(
        `/orcamentos/${emailModal.id}/enviar-email/`,
        { email: emailInput },
        { headers: { ...headers(), 'Content-Type': 'application/json' } }
      );
      toast.success(`Orçamento enviado para ${emailInput}.`);
      setEmailModal(null);
      setEmailInput('');
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao enviar o orçamento por email.');
    } finally {
      setSendingEmail(false);
    }
  };

  // ── Formatters ────────────────────────────────────────────────────────────────

  const formatCurrency = (v) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

  const formatDate = (str) => {
    if (!str) return '—';
    return new Date(str + 'T00:00:00').toLocaleDateString('pt-BR');
  };

  const linhaFiscalEmpresa = [
    minhaEmpresa?.cnpj ? `CNPJ: ${minhaEmpresa.cnpj}` : null,
    minhaEmpresa?.ie ? `IE: ${minhaEmpresa.ie}` : null,
    minhaEmpresa?.im ? `IM: ${minhaEmpresa.im}` : null,
  ].filter(Boolean).join('  |  ');

  const orcamentosFiltrados = clienteFiltroId
    ? orcamentos.filter((o) => String(o.cliente) === clienteFiltroId)
    : orcamentos;
  const nomeClienteFiltro = clientes.find((c) => String(c.id) === clienteFiltroId)?.nome;
  const limparFiltroCliente = () => {
    searchParams.delete('cliente');
    setSearchParams(searchParams);
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="produto-page">

      <div className="produto-page-header">
        <div className="produto-page-title-row">
          <h2 className="produto-page-title">Orçamentos</h2>
          {clienteFiltroId && (
            <span className="badge badge-ok" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              Filtrado por: {nomeClienteFiltro || `cliente #${clienteFiltroId}`}
              <span
                className="material-symbols-outlined"
                style={{ fontSize: 14, cursor: 'pointer' }}
                onClick={limparFiltroCliente}
              >
                close
              </span>
            </span>
          )}
        </div>
      </div>

      {/* ── Form card ──────────────────────────────────────── */}
      <div className="prd-card" ref={formRef}>
        <div className="prd-card-accent" />
        <div className="prd-form-body">

          <header className="prd-form-header">
            <h3 className="prd-form-title">
              {editingId ? `Editando Orçamento #${editingId}` : 'Novo Orçamento'}
            </h3>
            <p className="prd-form-sub">
              {editingId
                ? 'Altere os dados e salve para atualizar o orçamento.'
                : 'Preencha o cabeçalho e adicione os itens antes de salvar.'}
            </p>
          </header>

          <form onSubmit={handleSubmit}>

            {/* Row 1 — header fields */}
            <div className="orc-header-row">
              <div className="prd-field" style={{ flex: 3 }}>
                <label className="prd-label">À Sr(a).:</label>
                <select
                  className="prd-input"
                  value={form.cliente}
                  onChange={(e) => setForm({ ...form, cliente: e.target.value })}
                  required
                >
                  <option value="">Selecione um cliente...</option>
                  {clientes.map((c) => (
                    <option key={c.id} value={c.id}>{c.nome}</option>
                  ))}
                </select>
                {clientes.length === 0 && (
                  <span className="prd-label" style={{ color: '#ba1a1a', fontWeight: 400 }}>
                    Nenhum cliente cadastrado — <a href="/clientes/novo">cadastre um primeiro</a>.
                  </span>
                )}
              </div>
              <div className="prd-field">
                <label className="prd-label">Data</label>
                <input
                  className="prd-input"
                  type="date"
                  value={form.data}
                  onChange={(e) => setForm({ ...form, data: e.target.value })}
                  required
                />
              </div>
              <div className="prd-field">
                <label className="prd-label">Validade</label>
                <input
                  className="prd-input"
                  type="date"
                  value={form.validade}
                  onChange={(e) => setForm({ ...form, validade: e.target.value })}
                  required
                />
              </div>
              <div className="prd-field">
                <label className="prd-label">Status</label>
                <select
                  className="prd-input"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  <option value="Pendente">Pendente</option>
                  <option value="Aprovado">Aprovado</option>
                  <option value="Rejeitado">Rejeitado</option>
                </select>
              </div>
            </div>

            {/* Row 2 — item form */}
            <div className={`orc-item-section${editingItemIdx !== null ? ' orc-item-section--editing' : ''}`}>
              <p className="orc-section-label">
                <span className="material-symbols-outlined">
                  {editingItemIdx !== null ? 'edit' : 'add_circle'}
                </span>
                {editingItemIdx !== null ? `Editando Item ${editingItemIdx + 1}` : 'Adicionar Item'}
              </p>
              <div className="orc-item-row">
                <div className="prd-field orc-item-desc">
                  <label className="prd-label">Especificação / Descrição</label>
                  <textarea
                    className="prd-input prd-textarea"
                    rows={3}
                    placeholder="Descreva o produto ou serviço..."
                    value={especificacao}
                    onChange={(e) => setEspecificacao(e.target.value)}
                  />
                </div>
                <div className="prd-field orc-item-small">
                  <label className="prd-label">Tipo</label>
                  <select
                    className="prd-input"
                    value={tipoItem}
                    onChange={(e) => { setTipoItem(e.target.value); setProdutoItem(''); }}
                  >
                    {TIPOS_ITEM.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                {tipoItem === 'Produto' && (
                  <div className="prd-field orc-item-desc">
                    <label className="prd-label">Produto do estoque (opcional)</label>
                    <select
                      className="prd-input"
                      value={produtoItem}
                      onChange={(e) => setProdutoItem(e.target.value)}
                    >
                      <option value="">Item avulso (sem baixa de estoque)</option>
                      {produtos.map((p) => (
                        <option key={p.id} value={p.id}>{p.nome} — estoque: {p.quantidade_estoque}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="prd-field orc-item-small">
                  <label className="prd-label">Unidade</label>
                  <select
                    className="prd-input"
                    value={unidade}
                    onChange={(e) => setUnidade(e.target.value)}
                  >
                    {UNIDADES.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div className="prd-field orc-item-small">
                  <label className="prd-label">Qtd</label>
                  <input
                    className="prd-input"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0"
                    value={quantidade}
                    onChange={(e) => setQuantidade(e.target.value)}
                  />
                </div>
                <div className="prd-field orc-item-small">
                  <label className="prd-label">Valor Unit. (R$)</label>
                  <div className="prd-input-prefix-wrap">
                    <span className="prd-input-prefix">R$</span>
                    <input
                      className="prd-input prd-input--prefixed"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0,00"
                      value={valorUnitario}
                      onChange={(e) => setValorUnitario(e.target.value)}
                    />
                  </div>
                </div>
                <div className="orc-item-actions">
                  <button
                    type="button"
                    className="prd-btn-primary orc-btn-add"
                    onClick={handleSaveItem}
                  >
                    <span className="material-symbols-outlined">
                      {editingItemIdx !== null ? 'check' : 'add'}
                    </span>
                    {editingItemIdx !== null ? 'Atualizar' : 'Adicionar'}
                  </button>
                  {editingItemIdx !== null && (
                    <button
                      type="button"
                      className="prd-btn-cancel"
                      onClick={clearItemForm}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      Cancelar
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Items table */}
            {itens.length > 0 && (
              <div className="prd-table-wrap orc-items-table">
                <table className="prd-table">
                  <thead>
                    <tr>
                      <th className="center" style={{ width: '5%' }}>ITEM</th>
                      <th className="center" style={{ width: '8%' }}>TIPO</th>
                      <th>ESPECIFICAÇÃO</th>
                      <th className="center" style={{ width: '7%' }}>UNID.</th>
                      <th className="center" style={{ width: '7%' }}>QTD</th>
                      <th className="right"  style={{ width: '13%' }}>VALOR UNIT.</th>
                      <th className="right"  style={{ width: '13%' }}>V. TOTAL</th>
                      <th className="center" style={{ width: '7%' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {itens.map((item, index) => {
                      const isEditingRow = editingItemIdx === index;
                      return (
                        <tr key={index} style={isEditingRow ? { background: '#fffbeb' } : {}}>
                          <td className="center">{index + 1}</td>
                          <td className="center">
                            <span className={`badge ${item.tipo === 'Serviço' ? 'badge-ok' : 'badge-warn'}`}>
                              {item.tipo || 'Produto'}
                            </span>
                          </td>
                          <td>{item.descricao}</td>
                          <td className="center">{item.especificacao}</td>
                          <td className="center">{item.quantidade}</td>
                          <td className="right">{formatCurrency(item.valor_unitario)}</td>
                          <td className="right td-price">{formatCurrency(item.valor_total)}</td>
                          <td className="center">
                            <div className="td-actions" style={{ justifyContent: 'center' }}>
                              <button
                                type="button"
                                className={`prd-icon-btn${isEditingRow ? ' prd-icon-btn--active' : ''}`}
                                onClick={() => isEditingRow ? clearItemForm() : handleEditItem(index)}
                                title={isEditingRow ? 'Cancelar edição do item' : 'Editar item'}
                              >
                                <span className="material-symbols-outlined">
                                  {isEditingRow ? 'close' : 'edit'}
                                </span>
                              </button>
                              <button
                                type="button"
                                className="prd-icon-btn prd-icon-btn--danger"
                                onClick={() => removerItem(index)}
                                title="Remover item"
                              >
                                <span className="material-symbols-outlined">delete</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="orc-total-row">
                      <td colSpan={6} className="right">
                        <strong>TOTAL GERAL</strong>
                      </td>
                      <td className="right">
                        <strong className="prd-forecast-highlight">
                          {formatCurrency(calcularTotal())}
                        </strong>
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            <div className="prd-form-actions" style={{ gridColumn: 'unset', marginTop: '1.5rem' }}>
              {editingId ? (
                <button
                  type="button"
                  className="prd-btn-cancel"
                  onClick={handleCancelEdit}
                >
                  Cancelar Edição
                </button>
              ) : (
                <button
                  type="button"
                  className="prd-btn-cancel"
                  onClick={() => { setItens([]); setForm(FORM_EMPTY); }}
                >
                  Limpar
                </button>
              )}
              <button type="submit" className="prd-btn-save" disabled={saving}>
                <span className="material-symbols-outlined">
                  {editingId ? 'save' : 'add_circle'}
                </span>
                {saving
                  ? (editingId ? 'Salvando...' : 'Criando...')
                  : (editingId ? 'Salvar Alterações' : 'Salvar Orçamento')
                }
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* ── List card ──────────────────────────────────────── */}
      <div className="prd-card prd-table-card">
        <div className="prd-card-accent" />
        <div className="prd-table-toolbar">
          <span className="prd-table-count">
            {orcamentos.length} orçamento{orcamentos.length !== 1 ? 's' : ''} cadastrado{orcamentos.length !== 1 ? 's' : ''}
          </span>
          <button className="prd-icon-btn" onClick={fetchOrcamentos} title="Atualizar">
            <span className="material-symbols-outlined">refresh</span>
          </button>
        </div>
        <div className="prd-table-wrap">
          <table className="prd-table">
            <thead>
              <tr>
                <th className="center" style={{ width: '5%' }}>Nº</th>
                <th>Cliente</th>
                <th className="center">Data</th>
                <th className="center">Validade</th>
                <th className="right">Total</th>
                <th className="center">Status</th>
                <th className="center">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 7 }).map((__, j) => (
                      <td key={j}><div className="skeleton" /></td>
                    ))}
                  </tr>
                ))
              ) : orcamentosFiltrados.length === 0 ? (
                <tr>
                  <td colSpan={7} className="prd-empty">
                    {clienteFiltroId ? 'Nenhum orçamento para este cliente.' : 'Nenhum orçamento cadastrado.'}
                  </td>
                </tr>
              ) : (
                orcamentosFiltrados.map((orc) => {
                  const badge = STATUS_BADGE[orc.status] || { cls: 'badge-warn', label: orc.status };
                  const isEditing = editingId === orc.id;
                  return (
                    <tr key={orc.id} style={isEditing ? { background: '#fffbeb' } : {}}>
                      <td className="center" style={{ color: '#74777d', fontWeight: 600 }}>#{orc.id}</td>
                      <td className="td-name">{orc.cliente_nome}</td>
                      <td className="center">{formatDate(orc.data)}</td>
                      <td className="center">{formatDate(orc.validade)}</td>
                      <td className="right td-price">{formatCurrency(orc.valor_total)}</td>
                      <td className="center">
                        <span className={`badge ${badge.cls}`}>{badge.label}</span>
                      </td>
                      <td className="center">
                        <div className="td-actions" style={{ justifyContent: 'center' }}>
                          <button
                            className="prd-icon-btn"
                            onClick={() => handlePrint(orc)}
                            title="Imprimir"
                          >
                            <span className="material-symbols-outlined">print</span>
                          </button>
                          <button
                            className="prd-icon-btn"
                            onClick={() => handleEmail(orc)}
                            title="Enviar e-mail"
                          >
                            <span className="material-symbols-outlined">email</span>
                          </button>
                          <button
                            className="prd-icon-btn"
                            onClick={() => handleDownloadPdf(orc)}
                            title="Baixar PDF"
                          >
                            <span className="material-symbols-outlined">picture_as_pdf</span>
                          </button>
                          <button
                            className="prd-icon-btn"
                            onClick={() => {
                              const url = `${window.location.origin}/orcamento/${orc.token}`;
                              navigator.clipboard.writeText(url).then(() =>
                                toast.success('Link copiado! Envie ao cliente via WhatsApp ou e-mail.')
                              );
                            }}
                            title="Copiar link do portal do cliente"
                          >
                            <span className="material-symbols-outlined">share</span>
                          </button>
                          <button
                            className={`prd-icon-btn${isEditing ? ' prd-icon-btn--active' : ''}`}
                            onClick={() => isEditing ? handleCancelEdit() : handleEdit(orc)}
                            title={isEditing ? 'Cancelar edição' : 'Editar'}
                          >
                            <span className="material-symbols-outlined">
                              {isEditing ? 'close' : 'edit'}
                            </span>
                          </button>
                          <button
                            className="prd-icon-btn prd-icon-btn--danger"
                            onClick={() => handleDelete(orc.id)}
                            title="Excluir"
                          >
                            <span className="material-symbols-outlined">delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Email modal ────────────────────────────────────── */}
      {emailModal && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
          onClick={() => setEmailModal(null)}
        >
          <div
            style={{
              background: '#fff', borderRadius: '12px', padding: '2rem',
              width: '100%', maxWidth: '440px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 0.5rem', color: '#0D1B2A', fontWeight: 700 }}>Enviar Orçamento por E-mail</h3>
            <p style={{ margin: '0 0 1.5rem', color: '#74777d', fontSize: '14px' }}>
              Orçamento <strong>#{emailModal.id}</strong> — {emailModal.cliente_nome}
            </p>
            <form onSubmit={handleSendEmail}>
              <div className="prd-field" style={{ marginBottom: '1.5rem' }}>
                <label className="prd-label">E-mail do destinatário</label>
                <input
                  className="prd-input"
                  type="email"
                  placeholder="email@exemplo.com"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  autoFocus
                  required
                />
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="prd-btn-cancel"
                  onClick={() => setEmailModal(null)}
                >
                  Cancelar
                </button>
                <button type="submit" className="prd-btn-save" disabled={sendingEmail}>
                  <span className="material-symbols-outlined">send</span>
                  {sendingEmail ? 'Enviando...' : 'Enviar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Print template (hidden) ─────────────────────────── */}
      <div style={{ display: 'none' }}>
        <div ref={printRef}>
          {orcamentoImpressao && (
            <>
              <div className="print-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {minhaEmpresa?.logo && (
                    <img src={minhaEmpresa.logo} alt="" style={{ height: '56px', objectFit: 'contain' }} />
                  )}
                  <div>
                    <div className="empresa-nome">
                      {minhaEmpresa?.nome_fantasia || minhaEmpresa?.nome || ''}
                    </div>
                    {linhaFiscalEmpresa && (
                      <div className="empresa-info">{linhaFiscalEmpresa}</div>
                    )}
                  </div>
                </div>
                <div className="orc-info">
                  <div className="orc-title">ORÇAMENTO</div>
                  <div style={{ marginTop: 6 }}>Data: {formatDate(orcamentoImpressao.data)}</div>
                </div>
              </div>

              <div className="client-row">
                <span><strong>À Sr(a).:</strong> {orcamentoImpressao.cliente_nome}</span>
                <span><strong>Válido até:</strong> {formatDate(orcamentoImpressao.validade)}</span>
              </div>

              <table>
                <thead>
                  <tr>
                    <th style={{ width: '5%' }}>ITEM</th>
                    <th style={{ width: '48%' }}>ESPECIFICAÇÃO</th>
                    <th style={{ width: '8%' }}>UNID.</th>
                    <th style={{ width: '8%' }}>QTD</th>
                    <th style={{ width: '15%' }}>VALOR UNIT (R$)</th>
                    <th style={{ width: '16%' }}>V. TOTAL (R$)</th>
                  </tr>
                </thead>
                <tbody>
                  {(orcamentoImpressao.itens || []).map((item, idx) => (
                    <tr key={idx}>
                      <td className="center">{idx + 1}</td>
                      <td className="desc">{item.descricao}</td>
                      <td className="center">{item.especificacao}</td>
                      <td className="center">{item.quantidade}</td>
                      <td className="right">{formatCurrency(item.valor_unitario)}</td>
                      <td className="right">{formatCurrency(item.valor_total)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={5} className="right">TOTAL</td>
                    <td className="right">{formatCurrency(orcamentoImpressao.valor_total)}</td>
                  </tr>
                </tfoot>
              </table>

              {(minhaEmpresa?.razao_social || minhaEmpresa?.telefones?.length > 0) && (
                <div className="footer-sign">
                  <div className="sign-line">
                    {minhaEmpresa?.razao_social && <p>{minhaEmpresa.razao_social}</p>}
                    {minhaEmpresa?.telefones?.length > 0 && (
                      <p>TEL: {minhaEmpresa.telefones.join(' / ')}</p>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default OrcamentoList;
